import { useState, useRef, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  addDiversionaryDesc,
  uploadAboutUsImages,
  getAllDataWithFlag,
  updateDiversionaryDesc,
  deleteDiversionaryImage,
} from "../../../../services/CommonService";
import TipTapEditor from "../../../../Utils/TipTapEditor";
import { imgURL, aboutus_WIDTH, aboutus_HEIGHT } from "../../../Config";
import './Aboutus.css';

const Aboutus = () => {
  const [formData, setFormData] = useState({
    id: "",
    description: "",
  });
  const [forceRender, setForceRender] = useState(false);
  const [descriptionId, setDescriptionId] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState({
    initialLoad: false,
    descriptionSubmit: false,
    imageUpload: false,
    imageDelete: false,
  });

  const [viewMode, setViewMode] = useState("description");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    getAllDescriptionData();
    return () => { };
  }, []);

  // Description change control
  const handleDescriptionChange = (content) => {
    setFormData({ ...formData, description: content });
    if (validationErrors.description) {
      setValidationErrors((prev) => ({
        ...prev,
        description: "",
      }));
    }
  };

  // Handle multiple file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validImages = [];
    const maxSize = 1 * 1024 * 1024; // 1MB
    let validationErrors = [];

    const validateImage = (file) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          if (img.width < aboutus_WIDTH || img.height < aboutus_HEIGHT) {
            validationErrors.push(`"${file.name}" has invalid dimensions (${img.width} × ${img.height}). Expected ${aboutus_WIDTH} × ${aboutus_HEIGHT} px.`);
            resolve(null);
          } else if (file.size > maxSize) {
            validationErrors.push(`"${file.name}" exceeds the size limit of 1 MB.`);
            resolve(null);
          } else {
            resolve(file);
          }
        };
        img.onerror = () => {
          validationErrors.push(`"${file.name}" is not a valid image.`);
          resolve(null);
        };
        img.src = URL.createObjectURL(file);
      });
    };

    Promise.all(files.map(validateImage)).then((results) => {
      const filtered = results.filter((file) => file !== null);
      setSelectedImages(filtered);

      if (validationErrors.length > 0) {
        Swal.fire({
          icon: "warning",
          title: "Image Validation Failed",
          html: validationErrors.join("<br>"),
        });
      }
    });
  };

  const handleImageModalClose = () => {
    setShowImageModal(false);
    setSelectedImages([]);
  };

  // Toggle between description editor and gallery view
  const toggleViewMode = () => {
    setViewMode(viewMode === "description" ? "gallery" : "description");
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? uploadedImages.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === uploadedImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Delete image functionality
  const handleDeleteImage = async (imageId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading((prev) => ({ ...prev, imageDelete: true }));
        try {
          const response = await deleteDiversionaryImage(imageId);
          getAllDescriptionData();
          if (response.status === 200) {
            // Remove the deleted image from the state
            setUploadedImages(uploadedImages.filter(img => img.id !== imageId));

            // Adjust current image index if needed
            if (currentImageIndex >= uploadedImages.length - 1) {
              setCurrentImageIndex(Math.max(0, uploadedImages.length - 2));
            }
            Swal.fire(
              "Deleted!",
              "Your image has been deleted.",
              "success"
            );
          } else {
            Swal.fire(
              "Error!",
              "Failed to delete the image.",
              "error"
            );
          }
        } catch (error) {
          Swal.fire({
            title: "Error",
            text: error?.response?.data?.message || error?.message,
            icon: "error",
            confirmButtonText: "OK",
          });
        } finally {
          setIsLoading((prev) => ({ ...prev, imageDelete: false }));
        }
      }
    });
  };

  //Get all Data
  const getAllDescriptionData = async () => {
    setIsLoading((prev) => ({ ...prev, initialLoad: true }));
    try {
      const response = await getAllDataWithFlag(2);
      if (response.status === 200) {
        const diversionaryData = response?.data?.diversionary_trajectory[0];
        await setFormData({
          id: diversionaryData?.id,
          description: diversionaryData?.description,
        });
        await setDescriptionId(diversionaryData?.id);

        // Set uploaded images if they exist in the response
        if (diversionaryData?.images && diversionaryData?.images.length > 0) {
          const images = diversionaryData?.images.map((m) => {
            return {
              ...m,
              diversionary_image: `${imgURL}diversionary_image/${m.diversionary_image}`,
            };
          });
          setUploadedImages(images);
        }
        setForceRender((prev) => !prev);
      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading((prev) => ({ ...prev, initialLoad: false }));
    }
  };

  //validation form
  const validateForm = () => {
    const errors = {};
    if (!formData.description || formData.description.trim() === "<p></p>") {
      errors.description = "Description is required";
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0;
  }

  // Submit description
  const handleSubmitDiversionary = async (event) => {
    event.preventDefault();
    setError("");
    if (!validateForm()) return;
    setIsLoading((prev) => ({ ...prev, descriptionSubmit: true }));
    try {
      const formPayload = new FormData();
      formPayload.append("description", formData.description);
      let response;
      if (descriptionId) {
        response = await updateDiversionaryDesc(formData, descriptionId);
        if (response.status === 200) {
          setDescriptionId(response.data.id);
          Swal.fire({
            title: "Success!",
            text: "Description updated successfully.",
            icon: "success",
            confirmButtonText: "OK",
          });
          getAllDescriptionData();
        } else {
          setError("Failed to submit description.");
        }
      } else {
        response = await addDiversionaryDesc(formPayload);
        if (response.status === 201) {
          setDescriptionId(response.data.id);
          Swal.fire({
            title: "Success!",
            text: "Description submitted successfully.",
            icon: "success",
            confirmButtonText: "OK",
          });
          getAllDescriptionData();
        } else {
          setError("Failed to submit description.");
        }
      }
    } catch (error) {
      setError("Failed to connect to the server.");
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setError(error.response.data.message);
      }
    } finally {
      setIsLoading((prev) => ({ ...prev, descriptionSubmit: false }));
    }
  };

  // Upload multiple images
  const handleuploadAboutUsImages = async () => {
    if (!descriptionId) {
      setError("Please submit description first");
      return;
    }

    if (selectedImages.length === 0) {
      setError("Please select at least one image");
      return;
    }
    setIsLoading((prev) => ({ ...prev, imageUpload: true }));
    try {
      const formPayload = new FormData();
      formPayload.append("diversionary_id", descriptionId);

      selectedImages.forEach((image, index) => {
        formPayload.append(`diversionary_image`, image);
      });
      const response = await uploadAboutUsImages(formPayload);

      if (response.status === 201) {
        // Get updated images after upload
        getAllDescriptionData();
        setSelectedImages([]);
        setShowImageModal(false);

        Swal.fire({
          title: "Success!",
          text: "Images uploaded successfully",
          icon: "success",
          confirmButtonText: "OK",
        });
      } else {
        setError("Failed to upload images.");
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error?.message,
        icon: "error",
        confirmButtonText: "OK",
      });
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        setError(error.response.data.message);
      }
    } finally {
      setIsLoading((prev) => ({ ...prev, imageUpload: false }));
    }
  };

  // Render gallery view
  const renderGalleryView = () => {
    return (
      <div className="gallery-container">
        {/* Header with back button */}
        <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
          <div></div>
          <h5 className="mb-0">Image Gallery</h5>
          <Button
            onClick={toggleViewMode}
            className="btn-sm btn-warning"
          >
            <i className="bi bi-arrow-left"></i> Back
          </Button>
        </div>

        {/* Main image display */}
        {uploadedImages.length > 0 ? (
          <div className="position-relative text-center mb-3">
            <img
              src={uploadedImages[currentImageIndex]?.diversionary_image}
              alt="Gallery"
              className="img-fluid"
              style={{ maxHeight: "70vh", objectFit: "contain" }}
            />

            {/* Navigation buttons */}
            <div className="position-absolute top-50 start-0 translate-middle-y">
              <Button
                variant="light"
                className="rounded-circle shadow"
                onClick={handlePrevImage}
              >
                <i className="bi bi-chevron-left"></i>
              </Button>
            </div>
            <div className="position-absolute top-50 end-0 translate-middle-y">
              <Button
                variant="light"
                className="rounded-circle shadow"
                onClick={handleNextImage}
              >
                <i className="bi bi-chevron-right"></i>
              </Button>
            </div>

            {/* Image counter */}
            <div className="position-absolute bottom-0 start-50 translate-middle-x bg-dark bg-opacity-50 text-white px-3 py-1 rounded-top">
              {currentImageIndex + 1} / {uploadedImages.length}
            </div>

            {/* Delete button */}
            <Button
              variant="danger"
              className="position-absolute top-0 end-0 m-2"
              onClick={() => handleDeleteImage(uploadedImages[currentImageIndex].id)}
              disabled={isLoading.imageDelete}
            >
              {isLoading.imageDelete ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <i className="bi bi-trash"></i>
              )}
            </Button>
          </div>
        ) : (
          <div className="text-center p-5">
            <p>No images available. Please upload images first.</p>
          </div>
        )}

        {/* Thumbnail strip */}
        <div
          className="bg-light p-2 mt-2 overflow-auto"
          style={{ whiteSpace: "nowrap" }}
        >
          {uploadedImages.map((image, index) => (
            <div
              className="d-inline-block mx-1 position-relative"
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              style={{ cursor: "pointer" }}
            >
              <img
                src={image?.diversionary_image}
                alt={`Thumbnail ${index}`}
                className={`img-thumbnail ${currentImageIndex === index ? "border-primary border-3" : ""
                  }`}
                style={{
                  height: "100px",
                  width: "100px",
                  objectFit: "cover",
                }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render description editor view
  const renderDescriptionView = () => {
    return (
      <>
        <Form>
          <Form.Group className="m-3">
            <Form.Label className="fw-bold required">Description</Form.Label>
            <TipTapEditor
              key={forceRender}
              value={formData.description}
              onChange={handleDescriptionChange}
            />
            {validationErrors.description && (
              <div className="text-danger mt-1">{validationErrors.description}</div>
            )}
          </Form.Group>

          <div className="d-flex justify-content-between m-3">
            <Button
              type="button"
              className="btn btn-sm btn-warning"
              onClick={handleSubmitDiversionary}
              disabled={isLoading.descriptionSubmit}
            >
              {isLoading.descriptionSubmit ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                descriptionId ? "Update Description" : "Submit Description"
              )}
            </Button>
            <div>
              {uploadedImages.length > 0 && (
                <Button
                  type="button"
                  className="btn btn-sm btn-success me-2"
                  onClick={toggleViewMode}
                >
                  View Images
                </Button>
              )}
              <Button
                type="button"
                className="btn btn-sm btn-warning"
                onClick={() => setShowImageModal(true)}
                disabled={!descriptionId}
              >
                Add Images
              </Button>
            </div>
          </div>
        </Form>
      </>
    );
  };

  return (
    <>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Main content area - conditionally render based on viewMode */}
      {viewMode === "description" ? renderDescriptionView() : renderGalleryView()}

      {/* Image Upload Modal - Keep this separate */}
      <Modal show={showImageModal} onHide={handleImageModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Upload Images</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="required">Select Image (543 × 576 px, Max Size: 1MB)</Form.Label>
            <Form.Control
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </Form.Group>

          {selectedImages.length > 0 && (
            <div className="mt-3">
              <p>
                <strong>Selected {selectedImages.length} image(s):</strong>
              </p>
              <ul className="list-group">
                {selectedImages.map((image, index) => (
                  <li className="list-group-item" key={index}>
                    {image.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn btn-sm btn-secondary" onClick={handleImageModalClose}>
            Cancel
          </Button>
          <Button
            className="btn btn-sm btn-warning"
            onClick={handleuploadAboutUsImages}
            disabled={selectedImages.length === 0 || isLoading.imageUpload}
          >
            {isLoading.imageUpload ? (
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            ) : (
              "Upload Images"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Aboutus;